/**
 * Level 2 — Custom Table. Every parameter the engine has, on one screen.
 *
 * ============================ WHY THIS SCREEN IS ALLOWED =======================
 * The cards exist because eleven decisions before a hand is a bad front door. That is
 * not an argument for taking the eleven decisions away — a pro wants every one of them,
 * and `PRODUCT-FLOW-AND-PRICING.md` §2 says so in as many words. So this screen is the
 * old `TableSetup` form, grouped and kept: table, stacks, tournament, bots.
 *
 * The one thing it does that the form could not: SAVE AS A CARD. A pro's second visit
 * is three taps like everybody else's, to their own card, which appears on the Play tab
 * after Cash Table with a "yours" mark.
 *
 * Four of the form's fields are NOT here, and they are not lost: your seat, faster bots,
 * the seed and the seed lock are Level 3 — they decide how a table is dealt rather than
 * what is dealt — and they are in the Settings sheet, which this screen links to by
 * name rather than quietly dropping them.
 * ===============================================================================
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { tableOf, PROFILES } from "@engine/bot/profile";
import { Card } from "../components/ui";
import { DEFAULT_SETUP, type Setup } from "../lib/setup";
import { DIFFICULTY_WORDS, MODELLED_GUARDRAIL, difficultyOf, stepOfDifficulty } from "./poker-cards";
import { customSeatUrl, MIN_CUSTOM_BUYIN } from "./seat-url";
import { seatCost, stakesLabel, prizePool } from "../lib/wallet-client";
import { useWallet, BucksAmount } from "../lib/WalletContext";
import { level3Setup } from "../settings/SettingsSheet";
import { saveCard } from "../settings/prefs";
import "./rooms.css";

/**
 * The formats the table offers, and whether they exist yet. Carried over from the form
 * verbatim, including the disabled knockout button — `disabled` there is not styling,
 * it is a claim about the engine, and it comes off the day a bounty can be settled.
 */
const FORMATS: { id: string; label: string; note: string; disabled?: boolean }[] = [
  { id: "cash", label: "Cash", note: "Busted seats rebuy" },
  { id: "tournament", label: "Tournament", note: "Play until one is left" },
  { id: "knockout", label: "Knockout", note: "Not yet", disabled: true },
];

export function CustomTable() {
  const navigate = useNavigate();
  const [s, setS] = useState<Setup>(() => ({ ...DEFAULT_SETUP, ...level3Setup() }));
  const set = <K extends keyof Setup>(k: K, v: Setup[K]) => setS((p) => ({ ...p, [k]: v }));
  const [name, setName] = useState("");
  const [savedNote, setSavedNote] = useState<string | null>(null);

  /**
   * The big blind is typed as TEXT and clamped when the field is left.
   *
   * Carried over from the form with its reasoning intact: clamping on every keystroke
   * made any stake below 1 unreachable, because clearing the box to type "0.50" has the
   * first "0" rewritten before the "." arrives. A $0.25/$0.50 table could not be
   * configured at all.
   */
  const [bbText, setBbText] = useState(String(DEFAULT_SETUP.bigBlind));
  /* The buy-in is typed as text and clamped on blur, for the reason the big blind is:
     clearing the box to type a new number must not have the empty box clamped first. */
  const [buyinText, setBuyinText] = useState(String(DEFAULT_SETUP.buyin));

  const bots = tableOf(Math.max(0, s.seats - 1), s.difficulty);
  const counts = new Map<string, number>();
  for (const b of bots) counts.set(b.name, (counts.get(b.name) ?? 0) + 1);

  const step = stepOfDifficulty(s.difficulty);

  return (
    <div className="custom">
      <Card className="custom-card">
        <h1>Custom table</h1>
        <p className="muted">
          Everything the engine takes. Build one, sit down, and save it as a card if you
          want it again.
        </p>

        <section className="custom-group">
          <h2>Table</h2>
          <label className="custom-field">
            <span>Players</span>
            <input type="range" min={2} max={9} value={s.seats}
                   onChange={(e) => {
                     const seats = Number(e.target.value);
                     setS((p) => ({ ...p, seats, heroSeat: Math.min(p.heroSeat, seats - 1) }));
                   }} />
            <output className="num">{s.seats}-handed</output>
          </label>

          <fieldset className="custom-field">
            <legend>Format</legend>
            <div className="productpick-row" role="group" aria-label="Format">
              {FORMATS.map((f) => (
                <button key={f.id} type="button"
                        className={`product-btn${s.format === f.id ? " product-on" : ""}`}
                        aria-pressed={s.format === f.id}
                        disabled={f.disabled}
                        title={f.disabled ? "Knockout tables are not built yet" : undefined}
                        onClick={() => { if (!f.disabled) set("format", f.id as Setup["format"]); }}>
                  {f.label}
                  <em>{f.note}</em>
                </button>
              ))}
            </div>
          </fieldset>

          <label className="custom-field">
            <span>Big blind</span>
            <input
              type="number" min={0.02} step={0.02} value={bbText}
              onChange={(e) => {
                setBbText(e.target.value);
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n >= 0.02) {
                  setS((p) => {
                    const fraction = p.bigBlind > 0 ? p.ante / p.bigBlind : 0;
                    return { ...p, bigBlind: n, ante: Math.min(fraction, 0.25) * n };
                  });
                }
              }}
              onBlur={() => {
                const n = Number(bbText);
                const v = Number.isFinite(n) && n >= 0.02 ? n : 0.02;
                /* The ante is stored ABSOLUTE and edited as a fraction of the blind, so
                   changing the blind rescales it rather than silently changing what the
                   slider means. Same fix the form carries. */
                setS((p) => {
                  const fraction = p.bigBlind > 0 ? p.ante / p.bigBlind : 0;
                  return { ...p, bigBlind: v, ante: Math.min(fraction, 0.25) * v };
                });
                setBbText(String(v));
              }}
            />
          </label>
        </section>

        <section className="custom-group">
          <h2>Stacks</h2>
          <label className="custom-field">
            <span>Starting stack</span>
            <input type="range" min={10} max={250} step={5} value={s.stackBb}
                   onChange={(e) => set("stackBb", Number(e.target.value))} />
            <output className="num">{s.stackBb} big blinds</output>
          </label>
          <label className="check">
            <input type="checkbox" checked={s.resetStacks} disabled={s.format === "tournament"}
                   onChange={(e) => set("resetStacks", e.target.checked)} />
            Reset every stack to {s.stackBb}bb before each hand
          </label>
          <p className="muted small">
            On by default at a cash table, because the point of a chosen depth is
            practising at it: without this a bot that runs hot is playing 380 big blinds
            by hand twelve. A tournament never resets, and the box is disabled there.
          </p>
        </section>

        <section className="custom-group">
          <h2>Tournament</h2>
          <label className="custom-field">
            <span>Ante</span>
            <input type="range" min={0} max={25} value={Math.round((s.ante / s.bigBlind) * 100)}
                   onChange={(e) => set("ante", (Number(e.target.value) / 100) * s.bigBlind)} />
            <output className="num">
              {s.ante > 0 ? `${((s.ante / s.bigBlind) * 100).toFixed(0)}% of a blind` : "none"}
            </output>
          </label>
          {/*
            * PHASE 5, BOX 5: the buy-in, and the pool it funds.
            *
            * Only for a tournament, because a cash seat's price is arithmetic on the
            * stack and typing a second number beside a number it is supposed to equal
            * is how the two come to disagree. The pool is `buy-in x seats`, worked out
            * live from the two dials above rather than typed, for exactly the same
            * reason — and it is a BUCKS pool funding a table with no real prize, which
            * is the only footing on which it may take Bucks at all (decision D7).
            */}
          {s.format === "tournament" && (
            <>
              <label className="custom-field">
                <span>Buy-in</span>
                <input type="number" min={MIN_CUSTOM_BUYIN} step={10} value={buyinText}
                       onChange={(e) => {
                         setBuyinText(e.target.value);
                         const n = Math.round(Number(e.target.value));
                         if (Number.isFinite(n) && n >= MIN_CUSTOM_BUYIN) set("buyin", n);
                       }}
                       onBlur={() => {
                         const n = Math.round(Number(buyinText));
                         const v = Number.isFinite(n) && n >= MIN_CUSTOM_BUYIN ? n : DEFAULT_SETUP.buyin;
                         set("buyin", v);
                         setBuyinText(String(v));
                       }} />
                <output className="num">
                  <BucksAmount n={s.buyin} /> · pool <BucksAmount n={s.buyin * s.seats} />
                </output>
              </label>
              <p className="muted small">
                Tournament chips are not Bucks: this seat starts you with{" "}
                <b className="num">{(s.stackBb * s.bigBlind).toLocaleString("en-US")}</b> chips and
                costs what it says. The pool is what the seats put in, and it pays in Bucks.
              </p>
            </>
          )}
          <p className="muted small">
            Blind levels move by hand count, not by the clock. Turbo — faster levels and a
            shorter clock — is not dealt yet; the tournament cards carry the badge and say so.
          </p>
        </section>

        <section className="custom-group">
          <h2>Bots</h2>
          <label className="custom-field">
            <span>Opponents</span>
            <input type="range" min={1} max={5} step={1} value={step}
                   onChange={(e) => set("difficulty", difficultyOf(Number(e.target.value)))} />
            <output>{DIFFICULTY_WORDS[step - 1]}</output>
          </label>

          {/*
            * Who is at the table, by NAME, before you sit down.
            *
            * Kept from the form, and it is not decoration: the difficulty dial does not
            * say "how close to GTO these bots play" — it picks who sits down, and the
            * screen names them, so a player knows whether they are up against stations
            * or grinders before they buy in rather than after.
            */}
          <div className="custom-lineup">
            <h3>Who you will be playing</h3>
            <ul className="lineup">
              {[...counts.entries()].map(([who, n]) => {
                const p = PROFILES.find((x) => x.name === who)!;
                return (
                  <li key={who}>
                    <strong>{n > 1 ? `${n} × ` : ""}{who}</strong>
                    <span className="muted small"> {p.blurb}</span>
                  </li>
                );
              })}
            </ul>
            <p className="muted small">{MODELLED_GUARDRAIL}</p>
          </div>
        </section>

        <p className="muted small custom-level3">
          Your seat, faster bots and the seed are in{" "}
          <b>Settings</b> (the gear, top right) — they decide how a table is dealt, not
          what is dealt. <Link to="/fair">How the shuffle works</Link>.
        </p>

        <div className="custom-go">
          {/*
            * WHAT THIS SEAT COSTS, worked out in front of the player.
            *
            * Live from the dials, never a number typed beside them: at a cash table a
            * chip is a Buck, so the seat costs the starting stack and the arithmetic —
            * blind times depth — is the whole conversion. Somebody moving the stack
            * slider is moving the price, and they should be able to see that happen.
            *
            * PHASE 5, BOX 5: a tournament built here now has a buy-in too, typed in
            * the Tournament group above and clamped at `MIN_CUSTOM_BUYIN`. It used to
            * cost nothing and take nothing, which made it the one free seat in the
            * product and therefore the one everybody would have taken.
            */}
          <SeatPrice setup={s} />
          <button className="btn btn-wide" onClick={() => navigate(customSeatUrl({ ...s, ...level3Setup() }))}>
            Take a seat
          </button>
          <div className="custom-save">
            <label>
              <span className="muted small">Save as a card</span>
              <input type="text" value={name} placeholder="My 9-max bubble, tough"
                     onChange={(e) => setName(e.target.value)} />
            </label>
            <button className="btn-quiet" type="button"
                    disabled={!name.trim()}
                    onClick={() => {
                      const card = saveCard(name, { ...s });
                      setName("");
                      setSavedNote(`Saved as "${card.name}". It is on the Play tab.`);
                    }}>
              Save
            </button>
          </div>
          {savedNote && <p className="muted small" role="status">{savedNote}</p>}
        </div>
      </Card>
    </div>
  );
}


/** The live price of the table being built. */
function SeatPrice({ setup }: { setup: Setup }) {
  const { balance } = useWallet();
  const shape = {
    format: setup.format, seats: setup.seats,
    bigBlind: setup.bigBlind, stackBb: setup.stackBb,
    /* Box 5: the field the Tournament group now carries. `seatCost` ignores it at a
       cash table, where the price is the starting stack and nothing else. */
    buyin: setup.buyin,
  };
  const cost = seatCost(shape);
  const short = balance < cost;
  if (setup.format === "tournament") {
    return (
      <p className={"pcard-cost" + (short ? " pcard-cost-short" : "")}>
        Buy-in <BucksAmount n={cost} /> · pool <BucksAmount n={prizePool(shape)} />
        {short ? <> · you have <BucksAmount n={balance} /></> : null}
      </p>
    );
  }
  return (
    <p className={"pcard-cost" + (short ? " pcard-cost-short" : "")}>
      {stakesLabel(shape)} · buy in for <BucksAmount n={cost} />
      {short ? <> · you have <BucksAmount n={balance} /></> : null}
    </p>
  );
}
